import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Image, Upload, Trash2, Download, X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { getAuthHeaders, hasToken, API, BACKEND_URL } from "@/lib/api";

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventPhotoUpload({ eventId, isOwner, shareCode }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const fileInputRef = useRef(null);

  // Check if user is logged in
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          setCurrentUser(response.data);
        }
      } catch (error) {
        // Not logged in
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [eventId, shareCode]);

  const fetchPhotos = async () => {
    try {
      let response;
      if (shareCode) {
        // Public endpoint for guests - no auth required
        response = await axios.get(`${API}/public/event/${shareCode}/photos?limit=100`);
        // Response format: {photos: [...], total: N}
        setPhotos(response.data?.photos || []);
      } else {
        // Authenticated endpoint for owners
        response = await axios.get(`${API}/events/${eventId}/photos`, authConfig());
        setPhotos(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch photos:", error);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check each file
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß (max. 20MB)`);
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} ist keine Bilddatei`);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      let uploaded = 0;
      for (const file of files) {
        const formData = new FormData();
        formData.append("photo", file);

        // Determine uploader name: logged-in user > RSVP name > "Gast"
        let uploaderName = "Gast";
        if (currentUser?.name) {
          uploaderName = currentUser.name;
        } else {
          const savedRsvp = localStorage.getItem(`rsvp_${shareCode}`);
          if (savedRsvp) {
            uploaderName = JSON.parse(savedRsvp)?.name || "Gast";
          }
        }
        formData.append("guest_name", uploaderName);

        if (shareCode) {
          // Public upload endpoint for guests (or logged-in users viewing public page)
          const headers = { "Content-Type": "multipart/form-data" };
          const token = localStorage.getItem("auth_token");
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }
          await axios.post(`${API}/public/event/${shareCode}/photos`, formData, { headers });
        } else {
          // Authenticated endpoint for owners
          await axios.post(`${API}/events/${eventId}/photos`, formData, {
            ...authConfig(),
            headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
          });
        }

        uploaded++;
        setUploadProgress(Math.round((uploaded / files.length) * 100));
      }

      toast.success(`${files.length} Bild${files.length > 1 ? 'er' : ''} hochgeladen! 📸`);
      fetchPhotos();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/photos/${photoId}`, authConfig());
      toast.success("Bild gelöscht");
      fetchPhotos();
      if (selectedPhoto?.id === photoId) setSelectedPhoto(null);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const downloadPhoto = (photo) => {
    const link = document.createElement("a");
    link.href = `${BACKEND_URL}${photo.url}`;
    link.download = photo.filename || "photo.jpg";
    link.click();
  };

  const navigatePhoto = (direction) => {
    if (!selectedPhoto) return;
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = photos.length - 1;
    if (newIndex >= photos.length) newIndex = 0;
    setSelectedPhoto(photos[newIndex]);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
            <Image className="w-5 h-5 text-pink-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Foto-Galerie</h3>
            <p className="text-sm text-gray-500">{photos.length} Bilder</p>
          </div>
        </div>

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            size="sm"
            className="gap-2"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Bilder hochladen
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Photo Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Noch keine Bilder vorhanden</p>
          <p className="text-sm text-gray-400 mt-1">Lade Bilder hoch, um sie mit deinen Gästen zu teilen</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer bg-gray-100"
              onClick={() => setSelectedPhoto(photo)}
            >
              <img
                src={`${BACKEND_URL}${photo.thumbnail_url || photo.url}`}
                loading="lazy"
                decoding="async"
                alt=""
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedPhoto(photo); }}
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                {isOwner && (
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePhoto(photo.id); }}
                    className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
              <div className="absolute bottom-2 left-2 right-2 text-xs text-white bg-black/50 rounded px-2 py-1 opacity-0 group-hover:opacity-100 truncate">
                {photo.uploaded_by_name || "Gast"}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
              onClick={() => setSelectedPhoto(null)}
            >
              <X className="w-6 h-6" />
            </button>

            {/* Navigation */}
            {photos.length > 1 && (
              <>
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
                  onClick={(e) => { e.stopPropagation(); navigatePhoto(-1); }}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
                  onClick={(e) => { e.stopPropagation(); navigatePhoto(1); }}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Image */}
            <motion.img
              key={selectedPhoto.id}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={`${BACKEND_URL}${selectedPhoto.url}`}
              alt=""
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Info & Actions */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/50 backdrop-blur-md rounded-full px-4 py-2">
              <span className="text-white text-sm">
                {selectedPhoto.uploaded_by_name || "Gast"} • {selectedPhoto.created_at && formatDistanceToNow(new Date(selectedPhoto.created_at), { addSuffix: true, locale: de })}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); downloadPhoto(selectedPhoto); }}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
